import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, AuthTokenRecord, CaigenSettings, Viewer } from '../../lib/api';
import { useShell } from '../../app/shell-context';
import { WhaleuPreferences } from '../whaleu/WhaleuPreferences';
import { AcademicProfile } from './AcademicProfile';
import { PrivacyConsent } from './PrivacyConsent';
import { AccountDataRail } from './AccountDataRail';
import { DataConnectionPanel } from './DataConnectionPanel';
import { AccountPreferences } from './AccountPreferences';

interface AccountResponse {
  user: Viewer;
  settings: Record<string, unknown>;
  isAdmin: boolean;
  scope: 'full' | 'read';
  hasPassword: boolean;
}

export function AccountPage() {
  const { setViewer } = useShell();
  const [account, setAccount] = useState<AccountResponse>();
  const [tokens, setTokens] = useState<AuthTokenRecord[]>([]);
  const [message, setMessage] = useState('');
  const [newToken, setNewToken] = useState('');
  const [profile, setProfile] = useState({ displayName: '', school: '', grade: '', major: '', interests: '', weakSubjects: '', bio: '' });
  const [password, setPasswordValue] = useState({ first: '', second: '', current: '' });

  const loadTokens = useCallback(() => api<{ tokens: AuthTokenRecord[] }>('/auth/tokens').then((result) => setTokens(result.tokens || [])), []);

  const load = useCallback(async () => {
    try {
      const result = await api<AccountResponse>('/auth/me');
      setAccount(result);
      setViewer({ ...result.user, settings: result.settings, scope: result.scope, isAdmin: result.isAdmin, hasPassword: result.hasPassword });
      setProfile({
        displayName: result.user.displayName || '', school: result.user.profile?.school || '', grade: result.user.profile?.grade || '', major: result.user.profile?.major || '',
        interests: (result.user.profile?.interests || []).join('、'), weakSubjects: (result.user.profile?.weakSubjects || []).join('、'), bio: result.user.profile?.bio || '',
      });
      await loadTokens();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) window.location.replace(`/login?next=${encodeURIComponent('/account')}`);
      else setMessage(error instanceof Error ? error.message : '账户加载失败');
    }
  }, [loadTokens, setViewer]);

  useEffect(() => { void load(); }, [load]);

  const writeAllowed = account?.scope !== 'read';
  const splitTags = (value: string) => value.split(/[,，、]/).map((item) => item.trim()).filter(Boolean);

  const saveProfile = async () => {
    setMessage('');
    try {
      await api('/auth/profile', { method: 'PATCH', body: JSON.stringify({ ...profile, interests: splitTags(profile.interests), weakSubjects: splitTags(profile.weakSubjects) }) });
      setMessage('个人画像已保存，Agent 将使用新的上下文。');
      await load();
    } catch (error) { setMessage(error instanceof Error ? error.message : '保存失败'); }
  };

  const savePassword = async () => {
    if (password.first.length < 8) return setMessage('密码至少 8 位。');
    if (password.first !== password.second) return setMessage('两次输入的密码不一致。');
    if (account?.hasPassword && !password.current) return setMessage('请输入当前密码（或改用「忘记密码」走邮箱验证码）。');
    try {
      await api('/auth/password', { method: 'POST', body: JSON.stringify({ password: password.first, currentPassword: password.current }) });
      setPasswordValue({ first: '', second: '', current: '' });
      setMessage('密码已保存。');
      await load();
    } catch (error) { setMessage(error instanceof Error ? error.message : '密码保存失败'); }
  };

  const issueToken = async () => {
    try {
      const result = await api<{ token: string }>('/auth/tokens', { method: 'POST', body: JSON.stringify({ scope: 'full', ttlDays: 7, label: '应用' }) });
      setNewToken(result.token);
      await navigator.clipboard?.writeText(result.token).catch(() => undefined);
      await loadTokens();
    } catch (error) { setMessage(error instanceof Error ? error.message : '签发失败'); }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('吊销该令牌？此操作不可撤销。')) return;
    await api(`/auth/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
    await loadTokens();
  };

  const revokeOthers = async () => {
    if (!window.confirm('退出其他设备？当前浏览器会保持登录。')) return;
    try {
      const result = await api<{ revoked: number }>('/auth/tokens', { method: 'DELETE' });
      setMessage(`已退出 ${result.revoked} 个其他设备或应用。`);
      await loadTokens();
    } catch (error) { setMessage(error instanceof Error ? error.message : '操作失败'); }
  };

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    setViewer(null);
    window.location.replace('/login');
  };

  if (!account) return <div className="content-column account-loading"><p>{message || '正在读取账户…'}</p></div>;
  const user = account.user;

  return <div className="content-column account-page">
    <header className="page-header"><div><p>CAIGEN · IDENTITY</p><h1>账户与授权</h1><span>一个身份连接 Calendar、Agent 与经你授权的学习摘要。</span></div><button className="quiet-button" onClick={logout}>退出登录</button></header>
    <div className="account-layout">
      <aside><AccountDataRail user={user} isAdmin={account.isAdmin} scope={account.scope} /></aside>
      <div className="account-main">
        <DataConnectionPanel />
        <PrivacyConsent />
        <AcademicProfile displayName={user.displayName} profileContext={user.profile} writeAllowed={writeAllowed} />
        <section className="paper-card account-form" id="personal-profile"><h3>学习问卷 <small>兴趣与薄弱科目由你自己定义</small></h3><div className="field-grid">
          {([['displayName','称呼'],['school','学校'],['grade','年级'],['major','专业']] as const).map(([key, label]) => <label key={key}>{label}<input disabled={!writeAllowed} value={profile[key]} onChange={(event) => setProfile({ ...profile, [key]: event.target.value })} /></label>)}
        </div><label>兴趣方向<input disabled={!writeAllowed} value={profile.interests} onChange={(event) => setProfile({ ...profile, interests: event.target.value })} placeholder="用顿号分隔" /></label><label>薄弱科目<input disabled={!writeAllowed} value={profile.weakSubjects} onChange={(event) => setProfile({ ...profile, weakSubjects: event.target.value })} placeholder="用顿号分隔" /></label><label>一句话简介<textarea disabled={!writeAllowed} value={profile.bio} onChange={(event) => setProfile({ ...profile, bio: event.target.value })} maxLength={300} /></label><button className="primary-button" disabled={!writeAllowed} onClick={saveProfile}>保存画像</button></section>
        <AccountPreferences initial={account.settings as CaigenSettings} writeAllowed={writeAllowed} onChanged={(settings) => setAccount({ ...account, settings })} />
        <WhaleuPreferences writeAllowed={writeAllowed} onProfileSynced={load} />
        <section className="paper-card password-card"><h3>登录密码 <small>{account.hasPassword ? '已设置' : '未设置'}</small></h3><p className="empty-copy">{account.hasPassword ? '修改密码需先验证当前密码。' : '首次设置密码，无需验证。'}</p><div className="field-grid">{account.hasPassword ? <label>当前密码<input type="password" value={password.current} disabled={!writeAllowed} onChange={(event) => setPasswordValue({ ...password, current: event.target.value })} /></label> : null}<label>新密码<input type="password" value={password.first} disabled={!writeAllowed} onChange={(event) => setPasswordValue({ ...password, first: event.target.value })} /></label><label>确认密码<input type="password" value={password.second} disabled={!writeAllowed} onChange={(event) => setPasswordValue({ ...password, second: event.target.value })} /></label></div><button className="primary-button" disabled={!writeAllowed} onClick={savePassword}>保存密码</button></section>
        <section className="paper-card token-card" id="device-access"><header><div><small>DEVICE · ACCESS</small><h3>设备与应用令牌</h3><p>最多保留 10 个有效令牌；当前浏览器不会被误退出。</p></div><span>{tokens.length} / 10</span></header>{tokens.length ? <div className="token-list">{tokens.map((token) => <div className={`token-row${token.current ? ' current' : ''}`} key={token.id}><i>{token.label === '本机同步器' ? '同' : token.current ? '本' : '设'}</i><span><b>{token.label || '未命名设备'}{token.current ? ' · 当前' : ''}</b><small>{token.expired ? '已过期' : token.scope === 'read' ? '只读权限' : '完整权限'} · {token.lastUsedAt ? `最近使用 ${new Date(token.lastUsedAt).toLocaleDateString('zh-CN')}` : `签发于 ${token.createdAt ? new Date(token.createdAt).toLocaleDateString('zh-CN') : '未知时间'}`}</small><em>{token.sourceIp || '来源地址未知'} · {token.userAgent ? token.userAgent.slice(0, 54) : '客户端未上报'}</em></span>{token.current ? <strong>使用中</strong> : <button disabled={!writeAllowed} onClick={() => revoke(token.id)}>吊销</button>}</div>)}</div> : <p className="empty-copy">暂无设备令牌</p>}<div className="token-actions"><button className="quiet-button" disabled={!writeAllowed} onClick={issueToken}>签发 7 天应用令牌</button><button className="quiet-button danger" disabled={!writeAllowed || tokens.filter((token) => !token.current).length === 0} onClick={revokeOthers}>退出其他设备</button></div>{newToken && <output className="token-output"><small>仅显示一次</small><span>{newToken}</span></output>}</section>
        {message && <p className="account-message" role="status">{message}</p>}
      </div>
    </div>
  </div>;
}
